// Equipment affix -> stat bridge. See docs/adr/0024-equipment-bridge-smoke-bench.md.
//
// `aggregateEquipment` turns the affixes carried by equipped `Item`s into the
// stat contributions `resolveStats` folds into `ResolvedStats`. Only affixes
// whose effect lands on a `ResolvedStats` field the simulator already consumes
// are mapped (element damage% / resistance / max HP / attack speed / crit /
// accuracy / evasion / move speed). Affixes needing new combat mechanics
// (penetration / proc / conditional / slayer / minion / ...) are deferred to a
// separate ADR; `routeAffix` returns `null` for them.
//
// Routing is keyed by the `affix.<kind>.<family>.<key>` id schema (ADR 0017).
// Base item lookups use the content catalogue for weapon tier and Phase 5.x-A2
// armor/offHand base stats. `equipment-affixes.test.ts` cross-checks routing
// against `AFFIX_DEFINITIONS`: every affix is either routed or explicitly
// deferred, and each routed affix's flat/percent handling matches the
// definition's `valueKind`.
import { ITEM_BASES } from '@renavora/content';
import { ELEMENTS, TIER_BASE_MULTIPLIER, } from '@renavora/shared';
/** Non-physical elements carry `resistance`; physical mitigation uses `armor`. */
const RESIST_ELEMENTS = ELEMENTS.filter((el) => el !== 'physical');
const ELEMENT_SET = new Set(ELEMENTS);
const RESIST_ELEMENT_SET = new Set(RESIST_ELEMENTS);
/**
 * Resolve a weapon base's `tier` from its `baseId` (ADR 0025 — `weaponBaseMult`).
 * Scoped to `slot === 'weaponMain'` only: if an item carries `slot: 'weaponMain'`
 * but a baseId from another slot (corrupt / hand-built data), the lookup misses
 * and the multiplier falls back to 1 instead of silently applying.
 */
const WEAPON_BASE_TIER_BY_ID = new Map(ITEM_BASES.filter((base) => base.slot === 'weaponMain').map((base) => [base.id, base.tier]));
const ITEM_BASE_BY_ID = new Map(ITEM_BASES.map((base) => [base.id, base]));
/**
 * Map an affix id to its `ResolvedStats` target, or `null` if the affix needs
 * combat mechanics the simulator does not have yet (deferred, ADR 0024).
 * Keyed by the `affix.<kind>.<family>.<key>` schema (ADR 0017).
 */
export function routeAffix(affixId) {
    const parts = affixId.split('.');
    const family = parts[2];
    const key = parts[3];
    if (family === undefined || key === undefined)
        return null;
    switch (family) {
        case 'damage':
            // `affix.prefix.damage.<element>` for every element (physical included).
            // Unknown element keys route to `null` so the catalogue-coverage test
            // catches them instead of silently feeding a dead `elementBonus` key.
            return ELEMENT_SET.has(key) ? { stat: 'elementBonus', element: key } : null;
        case 'offense':
            if (key === 'attack_speed')
                return { stat: 'attackSpeed' };
            if (key === 'crit_chance')
                return { stat: 'critRate' };
            if (key === 'crit_damage')
                return { stat: 'critDamage' };
            if (key === 'accuracy')
                return { stat: 'accuracy' };
            return null; // cooldown_rate / dot_damage — deferred
        case 'defense':
            if (key === 'max_hp')
                return { stat: 'hpFlat' };
            if (key === 'all_resist')
                return { stat: 'resistanceAll' };
            if (key.startsWith('resist_')) {
                const element = key.slice('resist_'.length);
                return RESIST_ELEMENT_SET.has(element)
                    ? { stat: 'resistance', element: element }
                    : null;
            }
            return null; // armor_pct / shield_damage_reduction — deferred
        case 'utility':
            if (key === 'move_speed')
                return { stat: 'moveSpeed' };
            if (key === 'dodge')
                return { stat: 'evasion' };
            return null; // block_chance — deferred
        default:
            return null; // penetration / proc / conditional / status / slayer / ... — deferred
    }
}
/** Only `hpFlat` adds the raw value; every other target treats it as a percent. */
export function isFlatTarget(target) {
    return target.stat === 'hpFlat';
}
/**
 * Targets fed by `percent-mul` affixes (ADR 0017 §4.1): multiple contributions
 * compound multiplicatively, `(1 + a)(1 + b) - 1`, instead of summing. Every
 * other target combines additively. The catalogue-coverage test cross-checks
 * this against each affix's `valueKind`.
 */
export function isMultiplicativeTarget(target) {
    return (target.stat === 'elementBonus' || target.stat === 'attackSpeed' || target.stat === 'moveSpeed');
}
/** Fold a fresh contribution into a running total, additively or multiplicatively. */
function combine(current, amount, multiplicative) {
    return multiplicative ? (1 + current) * (1 + amount) - 1 : current + amount;
}
function emptyAggregate() {
    return {
        atkBonus: 0,
        weaponBaseMult: 1,
        hpFlat: 0,
        armor: 0,
        elementBonus: {},
        resistance: {},
        attackSpeed: 0,
        critRate: 0,
        critDamage: 0,
        accuracy: 0,
        evasion: 0,
        moveSpeed: 0,
    };
}
function applyAffix(agg, affix) {
    const target = routeAffix(affix.id);
    if (target === null)
        return;
    const raw = affix.values[0] ?? 0;
    const amount = isFlatTarget(target) ? raw : raw / 100;
    const mul = isMultiplicativeTarget(target);
    switch (target.stat) {
        case 'elementBonus':
            agg.elementBonus[target.element] = combine(agg.elementBonus[target.element] ?? 0, amount, mul);
            break;
        case 'resistance':
            agg.resistance[target.element] = combine(agg.resistance[target.element] ?? 0, amount, mul);
            break;
        case 'resistanceAll':
            for (const el of RESIST_ELEMENTS) {
                agg.resistance[el] = combine(agg.resistance[el] ?? 0, amount, mul);
            }
            break;
        case 'hpFlat':
            agg.hpFlat = combine(agg.hpFlat, amount, mul);
            break;
        case 'attackSpeed':
            agg.attackSpeed = combine(agg.attackSpeed, amount, mul);
            break;
        case 'critRate':
            agg.critRate = combine(agg.critRate, amount, mul);
            break;
        case 'critDamage':
            agg.critDamage = combine(agg.critDamage, amount, mul);
            break;
        case 'accuracy':
            agg.accuracy = combine(agg.accuracy, amount, mul);
            break;
        case 'evasion':
            agg.evasion = combine(agg.evasion, amount, mul);
            break;
        case 'moveSpeed':
            agg.moveSpeed = combine(agg.moveSpeed, amount, mul);
            break;
    }
}
function applyBaseStats(agg, item) {
    const base = ITEM_BASE_BY_ID.get(item.baseId);
    if (base === undefined)
        return;
    switch (base.baseStats.kind) {
        case 'armor':
            agg.armor += base.baseStats.armor;
            agg.hpFlat += base.baseStats.hpBonus;
            return;
        case 'offHand':
            agg.armor += base.baseStats.armor ?? 0;
            return;
        case 'weapon':
            return;
    }
}
/**
 * Aggregate the affixes on all equipped items into stat contributions.
 * Both the live `affixes` and the locked `sealed` affixes (ADR 0023) count;
 * every affix instance is summed (no dedup).
 *
 * ADR 0025: `weaponBaseMult` is derived from the equipped weapon's base Tier
 * via `TIER_BASE_MULTIPLIER` (docs/05-combat-sim §5.7.5). If more than one
 * `weaponMain` item is passed (unexpected — 1 slot 1 item), the first one wins.
 * If no `weaponMain` is equipped, the multiplier stays at 1.
 */
export function aggregateEquipment(equipment) {
    const agg = emptyAggregate();
    if (equipment === undefined)
        return agg;
    for (const item of equipment) {
        applyBaseStats(agg, item);
        for (const affix of item.affixes)
            applyAffix(agg, affix);
        for (const affix of item.sealed ?? [])
            applyAffix(agg, affix);
    }
    const weapon = equipment.find((item) => item.slot === 'weaponMain');
    if (weapon !== undefined) {
        const tier = WEAPON_BASE_TIER_BY_ID.get(weapon.baseId);
        if (tier !== undefined)
            agg.weaponBaseMult = TIER_BASE_MULTIPLIER[tier];
    }
    return agg;
}
/** Derive the attacker-side weapon element from the main weapon's damage affixes. */
export function deriveWeaponElement(equipment) {
    const weapon = equipment?.find((item) => item.slot === 'weaponMain');
    if (weapon === undefined)
        return 'physical';
    let best = null;
    for (const affix of [...weapon.affixes, ...(weapon.sealed ?? [])]) {
        const target = routeAffix(affix.id);
        if (target?.stat !== 'elementBonus' || target.element === 'physical')
            continue;
        const amount = (affix.values[0] ?? 0) / 100;
        if (amount <= 0)
            continue;
        if (best === null || amount > best.amount) {
            best = { element: target.element, amount };
        }
    }
    return best?.element ?? 'physical';
}
//# sourceMappingURL=equipment-affixes.js.map